/**
 * The opt-out confirmation page — the surface behind "Never contact me again" in every referee
 * invite footer (WP-B6).
 *
 * That footer link is not a nicety: an opt-out on outbound cold email is required by CAN-SPAM, the
 * NZ Unsolicited Electronic Messages Act and the Australian Spam Act, and this product's invites are
 * cold by construction — the candidate supplied the address, the referee never asked us for
 * anything. The endpoints existed and the link was being stamped into live mail, but the page it
 * pointed at existed in no surface, so the promise resolved to nothing. This is that page.
 *
 * ## Why it is a page and not a link that just works
 * The endpoints are POSTs, deliberately: mail clients, safe-link rewriters and spam scanners GET the
 * URLs in an email with no human involved, and a GET opt-out would suppress people who never clicked
 * anything. So the emailed link lands here and a human presses a button. **Nothing is sent on
 * mount** — that is the entire design, and a `useEffect` that "helpfully" auto-submits would undo
 * it.
 *
 * ## The states, and the one this page refuses to invent
 * confirm → working → done, plus an unreadable-link wall and a retryable failure. There is NO
 * "you were already unsubscribed" state: the endpoint answers a uniform 200 for any valid token
 * precisely so it cannot be used to test which addresses are on the list, so the client does not
 * know, and the `done` copy is written to be equally true either way. See `lib/suppression.ts`.
 *
 * ## Two routes, one page
 * `/never-contact` is what `ReferenceRequestOptions.NeverContactBaseUrl` stamps into the footer
 * today; `/unsubscribe` is the second endpoint's surface (a mail-client / List-Unsubscribe click),
 * mounted so that link has somewhere to land the moment a `List-Unsubscribe` header is added. Both
 * record the same global opt-out and differ only in the `source_detail` the backend writes, so both
 * show the same words — telling the referee two different stories would be inventing a distinction
 * the system does not make.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TerminalPage } from '@/components/TerminalPage';
import { Button } from '@/components/ui/button';
import { suppress, type SuppressionSurface } from '@/lib/suppression';

/** The query parameter `RefereeLinkBuilder.NeverContactUrl` appends: `{base}?token={token}`. */
const TOKEN_PARAM = 'token';

/** Where someone lands who cannot use the link at all. Must never be behind a broken token. */
const SUPPORT_EMAIL = 'support@bymyword.com';

type Phase = 'confirm' | 'working' | 'done' | 'unreadable';

export function OptOut({ surface }: { surface: SuppressionSurface }) {
  const [params] = useSearchParams();
  const token = params.get(TOKEN_PARAM) ?? '';

  // A missing or blank token is decided before the first render — there is nothing to POST, and a
  // confirm button that could only ever fail is the dead-end control this codebase keeps refusing.
  const [phase, setPhase] = useState<Phase>(() => (token.trim() ? 'confirm' : 'unreadable'));
  const [failed, setFailed] = useState(false);

  async function onConfirm() {
    setFailed(false);
    setPhase('working');
    const outcome = await suppress(surface, token);
    if (outcome === 'suppressed') {
      setPhase('done');
      return;
    }
    if (outcome === 'invalid_link') {
      setPhase('unreadable');
      return;
    }
    // Retryable: back to the prompt with the button live again, because a network blip must not cost
    // someone their opt-out.
    setPhase('confirm');
    setFailed(true);
  }

  if (phase === 'done') return <TerminalPage kind="opt-out-done" />;

  if (phase === 'unreadable') {
    return (
      <TerminalPage kind="opt-out-unreadable">
        <p className="font-body text-[13px] leading-relaxed text-ink-70">
          Email{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=Never%20contact%20me%20again`}
            className="font-medium text-coral-text underline underline-offset-2"
          >
            {SUPPORT_EMAIL}
          </a>{' '}
          and we&rsquo;ll add your address to the list by hand.
        </p>
      </TerminalPage>
    );
  }

  const working = phase === 'working';
  return (
    <TerminalPage kind="opt-out-confirm">
      <Button variant="coral" size="block" disabled={working} onClick={onConfirm}>
        Never contact me again
      </Button>

      {/*
        A polite live region that is ALWAYS mounted, not one that appears with its message: a region
        added to the DOM already containing text is unreliably announced, whereas a change inside an
        existing region is not. It is the only signal a screen-reader user gets that the tap landed —
        the visual one is the button going quiet, which they cannot see. Kept separate from the
        heading's own region (in TerminalPage) so the two never announce over each other: this one
        speaks while working, that one speaks when the outcome replaces the title.
      */}
      <p role="status" className="min-h-[1rem] font-display text-[12px] font-medium text-ink-quiet">
        {working ? 'One moment…' : ''}
      </p>

      {/*
        role="alert", not status: unlike every §5 page this genuinely did go wrong, and it is worth
        interrupting for — the referee is mid-action and the button they just pressed did nothing.
      */}
      {failed && (
        <p role="alert" className="text-center font-body text-[12.5px] font-medium text-coral-text">
          That didn&rsquo;t go through — please try again.
        </p>
      )}
    </TerminalPage>
  );
}

/** `/never-contact?token=…` — the invite footer's permanent opt-out link. */
export function NeverContactRoute() {
  return <OptOut surface="never-contact" />;
}

/** `/unsubscribe?token=…` — the mail-client / List-Unsubscribe surface of the same opt-out. */
export function UnsubscribeRoute() {
  return <OptOut surface="unsubscribe" />;
}
